/**
 * GS-06 — the note under the Sales Hierarchy fields (EventForm + EventCreateWizard).
 *
 *  - Says where the manager levels come from (the Lead/Event Rep's reporting chain) and
 *    names the levels that chain lacks (partial chains are normal until the AD sync loads
 *    the hierarchy).
 *  - When the saved levels differ from the lead's chain (e.g. the Lead was changed on the
 *    Sales Reps tab, or someone overrode a field), offers "Use lead's chain". Never applies
 *    it silently — the fields stay editable.
 *  - Flags other reps on the event's Sales Reps list whose reporting chain differs from the
 *    lead's (owner decision 10-01: lead's chain wins, flag the mismatch).
 */
import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../../services/supabase";
import {
  repsWithDifferentChain,
  sameLevels,
  walkReportingChain,
  hasAnyLevel,
  type ChainPosition,
  type ManagerLevels,
} from "../../utils/repReportingChain";
import { displaySalesPositionName } from "../../utils/salesPositionDisplay";

type PositionRow = ChainPosition & {
  title?: string | null;
  assigned_name?: string | null;
};

interface Props {
  salesPositions: readonly PositionRow[];
  leadId: string | null;
  current: ManagerLevels;
  /** existing event (edit / resumed draft): its Sales Reps list is checked for mismatches */
  eventId?: string | null;
  onUseLeadChain: (levels: ManagerLevels) => void;
}

const SalesHierarchyChainNote: React.FC<Props> = ({
  salesPositions,
  leadId,
  current,
  eventId,
  onUseLeadChain,
}) => {
  const { data: listRepIds = [] } = useQuery({
    queryKey: ["event-sales-reps-for-chain", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("event_sales_assignments")
        .select("sales_position_id")
        .eq("event_id", eventId as string);
      if (error) {
        return [] as string[];
      }
      return (data ?? [])
        .map((r) => r.sales_position_id as string | null)
        .filter((x): x is string => !!x);
    },
  });

  const nameOf = (id: string) => {
    const p = salesPositions.find((s) => s.id === id);
    return p ? displaySalesPositionName(p) : "(unknown position)";
  };

  const chain = useMemo(
    () => walkReportingChain(salesPositions, leadId),
    [salesPositions, leadId],
  );
  const mismatched = useMemo(
    () => repsWithDifferentChain(salesPositions, leadId, listRepIds),
    [salesPositions, leadId, listRepIds],
  );

  if (!leadId) {
    return null;
  }
  const leadName = nameOf(leadId);
  const leadHasChain = hasAnyLevel(chain);
  const missing = [
    !chain.tmId && "Territory Manager",
    !chain.rmId && "Regional Manager",
    !chain.sdId && "Sales Director",
  ].filter(Boolean) as string[];
  const differs = leadHasChain && !sameLevels(chain, current);

  return (
    <div className="mt-3 space-y-2 text-xs" data-testid="hierarchy-chain-note">
      {leadHasChain ? (
        <p className="text-gray-600">
          Managers come from {leadName}&apos;s reporting chain.
          {missing.length > 0 &&
            ` Not in the chain: ${missing.join(", ")} — pick manually if needed.`}
        </p>
      ) : (
        <p className="text-gray-600">
          {leadName} has no reporting chain on file, so the managers come from
          the partner account (or stay as entered).
        </p>
      )}
      {differs && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-blue-200 bg-blue-50 p-2 text-blue-800">
          <span>
            These managers differ from {leadName}&apos;s chain (TM:{" "}
            {chain.tmId ? nameOf(chain.tmId) : "—"}, RM:{" "}
            {chain.rmId ? nameOf(chain.rmId) : "—"}, SD:{" "}
            {chain.sdId ? nameOf(chain.sdId) : "—"}).
          </span>
          <button
            type="button"
            onClick={() => onUseLeadChain(chain)}
            className="rounded border border-blue-300 bg-white px-2 py-0.5 font-medium text-blue-700 hover:bg-blue-100"
          >
            Use lead&apos;s chain
          </button>
        </div>
      )}
      {mismatched.length > 0 && (
        <p
          className="rounded-md border border-amber-300 bg-amber-50 p-2 text-amber-800"
          data-testid="hierarchy-chain-mismatch"
        >
          ⚠️ Different reporting chain from the lead:{" "}
          {mismatched.map(nameOf).join(", ")}. The lead&apos;s chain is used.
        </p>
      )}
    </div>
  );
};

export default SalesHierarchyChainNote;
